import { useEffect, useState } from "react";
import { supabase } from "../auth";

const WORKER_URL = (import.meta.env.VITE_WORKER_URL as string | undefined) ??
  "https://garden-app-worker.tom-sandifordnam.workers.dev";

/** Fetch a private R2-backed photo through the Worker `/photos/serve`
 *  endpoint and expose it as a blob: URL safe to drop into an `<img src>`.
 *  See decisions/0019-photo-serve-via-worker-proxy.md. */
export function usePhotoUrl(r2Key: string | null | undefined): {
  url: string | null;
  error: string | null;
} {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!r2Key || !supabase) {
      setUrl(null);
      return;
    }
    let active = true;
    let objectUrl: string | null = null;
    setError(null);

    (async () => {
      try {
        const { data: sess } = await supabase!.auth.getSession();
        const token = sess.session?.access_token;
        if (!token) throw new Error("Not authenticated");
        const res = await fetch(
          `${WORKER_URL}/photos/serve?key=${encodeURIComponent(r2Key)}`,
          { headers: { authorization: `Bearer ${token}` } },
        );
        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          throw new Error(`Photo fetch failed (${res.status}): ${txt || res.statusText}`);
        }
        const blob = await res.blob();
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      } catch (err) {
        if (!active) return;
        setError(err instanceof Error ? err.message : String(err));
      }
    })();

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [r2Key]);

  return { url, error };
}
